import { BiotechOutlined } from '@mui/icons-material';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { dataTestIds } from 'src/constants/data-test-ids';
import { useGetAppointmentAccessibility } from 'src/features/visits/shared/hooks/useGetAppointmentAccessibility';
import { useAppointmentData } from 'src/features/visits/shared/stores/appointment/appointment.store';
import { DiagnosisDTO, EntryMode, getFormattedDiagnoses, InHouseOrderDetailPageItemDTO, LoadingState } from 'utils';
import { configResultPageContainerTestId } from '../../utils/test-ids';
import { InHouseLabResultCard } from './InHouseLabResultCard';

interface InHouseLabResultsProps {
  testDetails: InHouseOrderDetailPageItemDTO[] | undefined;
  setLoadingState: (loadingState: LoadingState) => void;
  onBack: () => void;
  entryMode: EntryMode;
}

export const InHouseLabResults: React.FC<InHouseLabResultsProps> = ({
  testDetails,
  setLoadingState,
  onBack,
  entryMode,
}) => {
  const navigate = useNavigate();
  const { isAppointmentReadOnly: isReadOnly } = useGetAppointmentAccessibility();
  const { encounter } = useAppointmentData();
  const queryClient = useQueryClient();

  // we sort the tests on the back end, most recent will always be first
  // const sortedOrders = inHouseOrders.sort((a, b) => compareDates(a.orderAddedDate, b.orderAddedDate));
  const mostRecentTest = testDetails?.[0];
  const openPdf = (): void => {
    if (mostRecentTest?.resultsPDFUrl) {
      window.open(mostRecentTest.resultsPDFUrl, '_blank');
    }
  };

  const diagnoses = testDetails?.reduce((acc: DiagnosisDTO[], detail) => {
    detail.diagnosesDTO.forEach((d) => {
      if (!acc.some((existing) => existing.code === d.code)) acc.push(d);
    });
    return acc;
  }, []);

  const buttonsToDisplay = testDetails?.reduce(
    (acc: { repeat: boolean; reflexTest: string | undefined }, detail) => {
      if (isReadOnly) return acc;

      const labDetails = detail.labDetails;
      if (labDetails.repeatable) acc.repeat = true;

      // todo labs if theres every a case where more than one test can be triggered, some work will have to be done here.
      // but also the design might change at that point so i wont handle now
      if (labDetails?.reflexAlert) {
        const reflexTestName = labDetails.reflexAlert.testName;
        const reflexTestCanonUrl = labDetails.reflexAlert.canonicalUrl;
        const reflexTestWasRun = !!testDetails.some((test) => {
          const testUrl = `${test.labDetails.adUrl}|${test.labDetails.adVersion}`;
          return testUrl === reflexTestCanonUrl;
        });
        // this drives if we show the button or not, if its already been run currently we don't show the button
        if (reflexTestName && !reflexTestWasRun) acc.reflexTest = reflexTestName;
      }

      return acc;
    },
    { repeat: false, reflexTest: undefined }
  );

  const handleRepeatOnClick = (): void => {
    if (encounter?.id) {
      // completely clears the cache and does not serve old data while the cache is refreshing
      // we do this to make sure that any changes to repeat tests are picked up properly
      queryClient.removeQueries({
        queryKey: ['inhouse lab resource search', encounter.id],
      });
    }

    navigate(`/in-person/${testDetails?.[0].appointmentId}/in-house-lab-orders/create`, {
      state: {
        testItemName: testDetails?.[0]?.testItemName,
        diagnoses: diagnoses,
        type: 'repeat',
      },
    });
  };

  const handleReflexTestOrderClick = (testName: string): void => {
    navigate(`/in-person/${testDetails?.[0].appointmentId}/in-house-lab-orders/create`, {
      state: {
        testItemName: testName,
        diagnoses: diagnoses,
        type: 'reflex',
      },
    });
  };

  const pageTitle = entryMode === EntryMode.Initial && (
    <Typography
      data-testid={dataTestIds.performTestPage.title}
      variant="h4"
      color="primary.dark"
      sx={{ mb: 3, fontWeight: 'bold' }}
    >
      Perform Test & Enter Results
    </Typography>
  );

  const resultButtons = entryMode === EntryMode.Edit && (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      {openPdf && (
        <Button
          data-testid={dataTestIds.resultPage.resultsPDF}
          variant="outlined"
          color="primary"
          sx={{ borderRadius: '50px', textTransform: 'none', mb: '12px' }}
          onClick={() => openPdf()}
          startIcon={<BiotechOutlined />}
          disabled={!mostRecentTest?.resultsPDFUrl}
        >
          Results PDF
        </Button>
      )}
      {buttonsToDisplay?.repeat && (
        <Button
          data-testid={dataTestIds.resultPage.repeatBtn}
          variant="outlined"
          onClick={handleRepeatOnClick}
          sx={{ borderRadius: '50px', px: 4 }}
        >
          Repeat
        </Button>
      )}
    </Box>
  );

  if (!testDetails) {
    return (
      <Box>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Test details not found
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box data-testid={configResultPageContainerTestId(entryMode)}>
      <Typography data-testid={dataTestIds.resultPage.diagnose} variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
        {getFormattedDiagnoses(diagnoses || [])}
      </Typography>

      {pageTitle}

      {resultButtons}

      {testDetails.map((test, idx) => (
        <InHouseLabResultCard
          key={`${idx}-${test.testItemName.split(' ').join('')}`}
          testDetails={test}
          setLoadingState={setLoadingState}
          entryMode={entryMode}
        ></InHouseLabResultCard>
      ))}

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
        <Button variant="outlined" onClick={onBack} sx={{ borderRadius: '50px', px: 4, textTransform: 'none' }}>
          Back
        </Button>
        {buttonsToDisplay?.reflexTest && (
          <Button
            data-testid={dataTestIds.resultPage.orderReflexTestBtn}
            variant="outlined"
            onClick={() => handleReflexTestOrderClick(buttonsToDisplay.reflexTest || '')}
            sx={{ borderRadius: '50px', px: 4, textTransform: 'none' }}
          >
            {`Order ${buttonsToDisplay.reflexTest}`}
          </Button>
        )}
      </Box>
    </Box>
  );
};
